import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LevelProgressProps } from 'shared/interfaces/level-progress/level-progress.interface';
import { ClaimQuestResponseDto } from 'shared/interfaces/quest/claim-quest-response.dto';
import { RewardProgressProps } from 'shared/interfaces/reward-progress/reward-progress.interface';
import LevelUp from 'src/components/CustomOverlay/LevelUp/LevelUp';
import RewardUnlocked from 'src/components/CustomOverlay/RewardUnlocked/RewardUnlocked';
import { markPwaMeaningfulEarn } from 'src/pwa/pwa';
import { checkIfRewardProgressIsComplete } from 'src/utils/reward-progress-check';

type LevelUpQueueItem = {
  levelReached: number;
  pointsEarned: number;
  rewardProgress?: RewardProgressProps | null;
};

type QuestClaimGamificationContextValue = {
  presentQuestClaimOutcomes: (response: ClaimQuestResponseDto) => void;
};

// Controls the quest-claim overlay flow.
const QuestClaimGamificationContext = createContext<QuestClaimGamificationContextValue | null>(null);

export const useQuestClaimGamification = (): QuestClaimGamificationContextValue => {
  const ctx = useContext(QuestClaimGamificationContext);
  if (!ctx) {
    throw new Error('useQuestClaimGamification must be used within QuestClaimGamificationProvider');
  }
  return ctx;
};

export const QuestClaimGamificationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [levelUpOverlayOpened, setLevelUpOverlayOpened] = useState(false);
  const [levelUpQueue, setLevelUpQueue] = useState<LevelUpQueueItem[]>([]);
  const [currentLevelUpIndex, setCurrentLevelUpIndex] = useState(0);
  const [rewardUnlockedOverlayOpened, setRewardUnlockedOverlayOpened] = useState(false);
  const [rewardProgressForModal, setRewardProgressForModal] = useState<RewardProgressProps | null>(null);
  const [claimFlowLevelProgress, setClaimFlowLevelProgress] = useState<LevelProgressProps | null>(null);

  const claimFlowLevelProgressRef = useRef<LevelProgressProps | null>(null);
  const pendingQuestRewardProgressRef = useRef<RewardProgressProps | null>(null);
  const levelUpQueueRef = useRef<LevelUpQueueItem[]>([]);
  const currentLevelUpIndexRef = useRef(0);

  useEffect(() => {
    levelUpQueueRef.current = levelUpQueue;
  }, [levelUpQueue]);

  useEffect(() => {
    currentLevelUpIndexRef.current = currentLevelUpIndex;
  }, [currentLevelUpIndex]);

  const buildAndOpenLevelUps = useCallback((lp: LevelProgressProps) => {
    // Show one level-up modal per reached level.
    claimFlowLevelProgressRef.current = lp;
    setClaimFlowLevelProgress(lp);

    if (!lp.isUserLeveledUp || !lp.leveledUpCount || !lp.startingLevel) {
      setLevelUpQueue([]);
      setCurrentLevelUpIndex(0);
      setLevelUpOverlayOpened(true);
      return;
    }

    if (lp.rewardProgress && lp.rewardProgress.length > 0) {
      setLevelUpQueue(
        lp.rewardProgress.map((rewardProgress, index) => ({
          levelReached: lp.startingLevel + index + 1,
          pointsEarned: rewardProgress.pointsEarned || 0,
          rewardProgress,
        }))
      );
      setCurrentLevelUpIndex(0);
      setLevelUpOverlayOpened(true);
      return;
    }

    const levelsGained = lp.leveledUpCount;
    const startingLevel = lp.startingLevel;
    const totalPointsEarned = lp.pointsEarnedForEachLevelUp || [];
    const queue: LevelUpQueueItem[] = [];

    for (let i = 0; i < levelsGained; i++) {
      const currentLevel = startingLevel + i;
      queue.push({
        levelReached: currentLevel + 1,
        pointsEarned: totalPointsEarned[i] ?? 0,
        rewardProgress: lp.rewardProgress?.[i],
      });
    }

    setLevelUpQueue(queue);
    setCurrentLevelUpIndex(0);
    setLevelUpOverlayOpened(true);
  }, []);

  const presentQuestClaimOutcomes = useCallback(
    (response: ClaimQuestResponseDto) => {
      markPwaMeaningfulEarn('quest_claimed');
      // Level-ups come first; rewardUnlocked modal comes after.
      pendingQuestRewardProgressRef.current = response.rewardProgress;

      if (response.levelProgress?.isUserLeveledUp) {
        buildAndOpenLevelUps(response.levelProgress);
        return;
      }

      claimFlowLevelProgressRef.current = null;
      setClaimFlowLevelProgress(null);

      if (checkIfRewardProgressIsComplete(response.rewardProgress)) {
        setRewardProgressForModal(response.rewardProgress);
        setRewardUnlockedOverlayOpened(true);
      }
    },
    [buildAndOpenLevelUps]
  );

  const handleLevelUpClose = useCallback(() => {
    // Move through the level-up queue, then show rewardUnlocked modal if needed.
    const queue = levelUpQueueRef.current;
    const idx = currentLevelUpIndexRef.current;
    const lp = claimFlowLevelProgressRef.current;
    const questRp = pendingQuestRewardProgressRef.current;

    if (queue.length > 0 && idx < queue.length - 1) {
      setCurrentLevelUpIndex(idx + 1);
      return;
    }

    const lastIdx = idx;
    setLevelUpOverlayOpened(false);
    setLevelUpQueue([]);
    setCurrentLevelUpIndex(0);

    const embedded = lp?.rewardProgress?.[lastIdx];
    const questComplete = checkIfRewardProgressIsComplete(questRp);
    const embeddedComplete = checkIfRewardProgressIsComplete(embedded);

    if (questComplete && questRp) {
      setRewardProgressForModal(questRp);
      setRewardUnlockedOverlayOpened(true);
    } else if (embeddedComplete && embedded) {
      setRewardProgressForModal(embedded);
      setRewardUnlockedOverlayOpened(true);
    }

    claimFlowLevelProgressRef.current = null;
    setClaimFlowLevelProgress(null);
    pendingQuestRewardProgressRef.current = null;
  }, []);

  const handleRewardUnlockedClose = useCallback(() => {
    setRewardUnlockedOverlayOpened(false);
    setRewardProgressForModal(null);
  }, []);

  const value = useMemo(() => ({ presentQuestClaimOutcomes }), [presentQuestClaimOutcomes]);

  const levelUpKey =
    levelUpQueue.length > 0 && currentLevelUpIndex < levelUpQueue.length
      ? `quest-claim-level-up-${levelUpQueue[currentLevelUpIndex].levelReached}-${currentLevelUpIndex}`
      : `quest-claim-level-up-${claimFlowLevelProgress?.currentLevel ?? 0}`;

  return (
    <QuestClaimGamificationContext.Provider value={value}>
      {children}
      <LevelUp
        key={levelUpKey}
        isOpen={levelUpOverlayOpened}
        onClose={handleLevelUpClose}
        levelReached={
          levelUpQueue.length > 0 && currentLevelUpIndex < levelUpQueue.length
            ? levelUpQueue[currentLevelUpIndex].levelReached
            : (claimFlowLevelProgress?.currentLevel ?? 0)
        }
        pointsEarned={
          levelUpQueue.length > 0 && currentLevelUpIndex < levelUpQueue.length
            ? levelUpQueue[currentLevelUpIndex].pointsEarned
            : (claimFlowLevelProgress?.pointsEarnedForEachLevelUp?.[0] ?? claimFlowLevelProgress?.xpEarned ?? 0)
        }
        rewardProgress={
          levelUpQueue.length > 0 && currentLevelUpIndex < levelUpQueue.length
            ? levelUpQueue[currentLevelUpIndex].rewardProgress
            : claimFlowLevelProgress?.rewardProgress?.[currentLevelUpIndex]
        }
      />
      <RewardUnlocked
        isOpen={rewardUnlockedOverlayOpened}
        onClose={handleRewardUnlockedClose}
        rewardProgress={rewardProgressForModal}
      />
    </QuestClaimGamificationContext.Provider>
  );
};
